import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Cross, BookOpen, Flame, Sunrise, HandHeart, Sparkles, Check } from "lucide-react";

const KINDS = [
    { k: "rosary", label: "Pray the Rosary", icon: <Cross size={16} /> },
    { k: "mass", label: "Pray in the Mass", icon: <BookOpen size={16} /> },
    { k: "offer_mass", label: "Offer Holy Mass", icon: <Sparkles size={16} /> },
    { k: "adoration", label: "Pray in Adoration", icon: <Flame size={16} /> },
    { k: "divine_mercy", label: "Divine Mercy", icon: <HandHeart size={16} /> },
    { k: "novena", label: "Special Novena", icon: <Sunrise size={16} /> },
];

const NOVENAS = [
    { v: "mary", l: "Blessed Virgin Mother Mary" },
    { v: "infant_jesus", l: "Infant Jesus" },
    { v: "st_joseph", l: "St Joseph" },
    { v: "st_antony", l: "St Antony" },
];

export default function SpecialRequests() {
    const { user } = useAuth();
    const [kind, setKind] = useState("rosary");
    const [novenaTo, setNovenaTo] = useState("mary");
    const [intention, setIntention] = useState("");
    const [items, setItems] = useState([]);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        api.get("/special-requests").then(({ data }) => setItems(data)).catch(() => {});
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!user?.id) { window.location.href = "/login"; return; }
        const body = { kind, intention };
        if (kind === "novena") body.novena_to = novenaTo;
        const { data } = await api.post("/special-requests", body);
        setItems((p) => [data, ...p]);
        setIntention("");
        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 2500);
    };

    return (
        <div className="parchment min-h-[calc(100vh-4rem)]">
            <div className="mx-auto max-w-7xl px-6 md:px-10 py-12">
                <div className="text-xs tracking-[0.28em] uppercase" style={{ color: "#7F1D1D" }}>Special Request Model</div>
                <h1 className="font-display text-4xl sm:text-5xl mt-2" style={{ color: "#1E1B4B" }}>Request a particular form of prayer.</h1>

                <div className="mt-8 grid lg:grid-cols-12 gap-6">
                    <form onSubmit={submit} className="lg:col-span-7 p-card p-6" data-testid="special-form">
                        <div className="grid sm:grid-cols-2 gap-2.5">
                            {KINDS.map((k) => (
                                <button key={k.k} type="button"
                                        onClick={() => setKind(k.k)}
                                        className={`p-card p-3 text-left flex items-center gap-3 ${kind === k.k ? "ring-2" : ""}`}
                                        style={{ ringColor: "#D4AF37", borderColor: kind === k.k ? "#D4AF37" : "#E5E0D8" }}
                                        data-testid={`special-kind-${k.k}`}>
                                    <div className="h-9 w-9 rounded-full flex items-center justify-center"
                                         style={{ background: kind === k.k ? "#1E1B4B" : "#F5F0E6", color: kind === k.k ? "#D4AF37" : "#7F1D1D" }}>
                                        {k.icon}
                                    </div>
                                    <div className="font-display text-base" style={{ color: "#1E1B4B" }}>{k.label}</div>
                                </button>
                            ))}
                        </div>

                        {kind === "novena" && (
                            <div className="mt-5">
                                <label className="block text-xs uppercase tracking-[0.22em] mb-2" style={{ color: "#7F1D1D" }}>Novena to</label>
                                <div className="flex flex-wrap gap-2">
                                    {NOVENAS.map((n) => (
                                        <button type="button" key={n.v} onClick={() => setNovenaTo(n.v)}
                                                className={`chip ${novenaTo === n.v ? "active" : ""}`}
                                                data-testid={`special-novena-${n.v}`}>
                                            {n.l}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="mt-5">
                            <label className="block text-xs uppercase tracking-[0.22em] mb-2" style={{ color: "#7F1D1D" }}>Your intention</label>
                            <textarea required rows={4} value={intention} onChange={(e) => setIntention(e.target.value)}
                                      className="w-full px-4 py-3 rounded-2xl border"
                                      style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                      data-testid="special-intention-input" />
                        </div>

                        <button type="submit" className="mt-5 btn-primary" data-testid="special-submit-button">
                            Offer this request
                        </button>
                        {submitted && (
                            <div className="mt-3 text-sm flex items-center gap-1" style={{ color: "#7F1D1D" }} data-testid="special-success">
                                <Check size={14} /> Your request has been entrusted to the warriors.
                            </div>
                        )}
                    </form>

                    <div className="lg:col-span-5">
                        <div className="p-card p-6">
                            <div className="font-display text-2xl" style={{ color: "#1E1B4B" }}>Recent special requests</div>
                            <div className="mt-4 space-y-3">
                                {items.length === 0 && <div className="text-sm text-neutral-500">No special requests yet.</div>}
                                {items.slice(0, 12).map((r) => (
                                    <div key={r.id} className="border-b pb-3 last:border-b-0" style={{ borderColor: "#E5E0D8" }} data-testid={`special-item-${r.id}`}>
                                        <div className="text-[10px] tracking-[0.2em] uppercase" style={{ color: "#7F1D1D" }}>
                                            {r.kind}{r.novena_to ? ` · ${r.novena_to}` : ""}
                                        </div>
                                        <div className="text-sm mt-1" style={{ color: "#1E1B4B" }}>{r.intention}</div>
                                        <div className="text-xs mt-1" style={{ color: "#525252" }}>— {r.requester_name}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
